import { Grid } from '@mui/material';
import {
    CollapsiblePaper,
    LoadingPage,
    Page,
    PageTitle,
    PaperTitle,
    withJob,
} from 'cleveron-web-components';
import { parse } from 'date-fns';
import React, { useEffect } from 'react';
import { useForm } from 'react-final-form';
import { defineMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import {
    addIssueComment as addIssueCommentAction,
    deleteIssueComment as deleteIssueCommentAction,
    editIssueComment as editIssueCommentAction,
} from '../../../../actions/collections/issues';
import {
    fetchMaintenanceIssue as fetchMaintenanceIssueAction,
    updateMaintenanceIssue as updateMaintenanceIssueAction,
    uploadMaintenanceIssueAttachment as uploadMaintenanceIssueAttachmentAction,
} from '../../../../actions/collections/maintenanceIssues';
import { ISSUE_STATUSES } from '../../../../constants/issueStatuses';
import { reactFinalFormHOC, useFormValues } from '../../../../form';
import useAccess from '../../../../utils/useAccess';
import usePermissions from '../../../../utils/usePermissions';
import Comments from '../../../Comments';
import ErrorPage from '../../../ErrorPage';
import Attachments from './Attachments';
import Maintenance from './Maintenance';
import TaskInfo from './TaskInfo';

const Task = ({
    addIssueComment,
    editIssueComment,
    deleteIssueComment,
    fetchMaintenanceIssue,
    id: domId,
    issue,
    updateMaintenanceIssue,
    uploadMaintenanceIssueAttachment,
}) => {
    const { isCleveron } = useAccess();
    const { canUpdate } = usePermissions({
        canUpdate: 'maintenance_issue_update',
    });

    const {
        actualDate: initialActualDate,
        attachments,
        comments,
        jiraIssueKey,
        summary,
    } = issue || {};
    const { getState } = useForm();
    const { actualDate, statusId } = useFormValues(['actualDate', 'statusId']);

    useEffect(() => {
        const { pristine } = getState();
        if (!pristine && statusId) {
            updateMaintenanceIssue({
                actualDate,
                fieldsToClear:
                    initialActualDate && actualDate === null ? ['actualDate'] : undefined,
                id: issue.id,
                statusId,
            });
        }
    }, [actualDate, statusId, issue.id, updateMaintenanceIssue, getState, initialActualDate]);

    const error = issue && issue.error;
    if (error) {
        return <ErrorPage error={error} id={domId} />;
    }

    if (!issue) {
        return null;
    }

    const disabled = !canUpdate || (!isCleveron && statusId === ISSUE_STATUSES.CLOSED);

    return (
        <Page id={domId}>
            <PageTitle
                back={!!window.BACK_BUTTON}
                id={`${domId}PageTitle`}
                subtitle={jiraIssueKey}
                title={summary}
            />
            <TaskInfo disabled={disabled} id={`${domId}TaskInfo`} issue={issue} />
            <Maintenance
                disabled={disabled}
                id={`${domId}Maintenance`}
                issueId={issue.id}
                modules={issue.modules}
            />
            <Attachments
                attachments={attachments}
                disabled={disabled}
                fetchJiraIssue={fetchMaintenanceIssue}
                id={`${domId}Attachments`}
                issueKey={issue.id}
                uploadJiraIssueAttachment={uploadMaintenanceIssueAttachment}
            />
            <CollapsiblePaper
                hasMoveDeleteButtons={false}
                headerComponent={
                    <Grid container item xs>
                        <PaperTitle
                            id={`${domId}CommentsTitle`}
                            titleKey={
                                defineMessage({
                                    defaultMessage: 'Comments',
                                    id: 'maintenance.comments',
                                }).id
                            }
                        />
                    </Grid>
                }
            >
                <Comments
                    addComment={addIssueComment}
                    comments={comments}
                    deleteComment={!disabled && deleteIssueComment}
                    disabled={!canUpdate}
                    domId={`${domId}Comments`}
                    editComment={!disabled && editIssueComment}
                    fetchComments={fetchMaintenanceIssue}
                    itemId={issue.id}
                />
            </CollapsiblePaper>
        </Page>
    );
};

const TaskForm = reactFinalFormHOC()(Task);

function mapStateToProps(state, { itemId }) {
    const issue = state.maintenanceIssues.collection[itemId];

    return {
        initialValues: {
            actualDate: issue?.actualDate,
            actualDateTmp: issue?.actualDate && parse(issue.actualDate, 'yyyy-MM-dd', new Date()),
            statusId: issue?.status?.id,
        },
        issue,
    };
}

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            addIssueComment: addIssueCommentAction,
            deleteIssueComment: deleteIssueCommentAction,
            editIssueComment: editIssueCommentAction,
            fetchMaintenanceIssue: fetchMaintenanceIssueAction,
            updateMaintenanceIssue: updateMaintenanceIssueAction,
            uploadMaintenanceIssueAttachment: uploadMaintenanceIssueAttachmentAction,
        },
        dispatch
    );

export default compose(
    connect(null, mapDispatchToProps),
    withJob({
        LoadingComponent: ({ id }) => <LoadingPage id={`${id}Loading`} />,
        work: ({ fetchMaintenanceIssue, itemId }) =>
            Promise.all([fetchMaintenanceIssue({ id: itemId })]),
    })
)(connect(mapStateToProps)(TaskForm));
